import React from "react";
import { Button } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import RemoteSelect from "./RemoteSelect";
import { fetchUsers, pasteUsers, fetchItineraries } from "../fetchers";

const itinOption = (o) => (
  <div style={{ lineHeight: 1.3, padding: "2px 0" }}>
    <div style={{ whiteSpace: "normal" }}>{o.headline || o.label}</div>
    <div style={{ fontSize: 12, color: "#5f6680" }}>ID: {o.value}{o.owner ? ` · ${o.owner}` : ""}</div>
  </div>
);
const userOption = (o, hint) => (
  <div style={{ lineHeight: 1.3 }}>
    <div>#{o.value} {o.name}</div>
    <div style={{ fontSize: 12, color: hint ? "#b7372f" : "#5f6680" }}>{hint || o.email}</div>
  </div>
);

/**
 * "Who gets which itinerary" for ONE itinerary_download variable. Each row = several users
 * who all get the same ONE itinerary. A user can be in only one row of this variable.
 */
const ItineraryMapping = ({ rows, onChange, recipientIds }) => {
  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const usedElsewhere = (i) => rows.flatMap((r, idx) => (idx === i ? [] : r.users.map((u) => u.value)));
  const inRecipients = new Set(recipientIds);

  return (
    <div className="itinmap">
      <div className="rtop">
        <b>Who gets which itinerary</b>
        <Button icon={<PlusOutlined />} onClick={() => onChange([...rows, { users: [], itinerary: null }])}>Add Row</Button>
      </div>
      <div className="note" style={{ marginBottom: 10 }}>
        Each row is a group of users who all get the <b>same one itinerary</b> from this link, if they're also added in Recipients below. Add another row for a different group with a different itinerary.
      </div>
      {rows.map((r, i) => {
        const dupes = r.users.filter((u) => usedElsewhere(i).includes(u.value));
        const notRecip = r.users.filter((u) => !inRecipients.has(u.value));
        const problems = [
          !r.users.length && "Select at least one user.",
          !r.itinerary && "Pick one itinerary for this group.",
          dupes.length && `${dupes.map((u) => u.name || u.label).join(", ")} is already in another row. One itinerary per user from this variable.`,
        ].filter(Boolean);
        return (
          <div className="rrow2" key={i}>
            <RemoteSelect ariaLabel={`Users for row ${i + 1}`} mode="multiple" value={r.users} onChange={(users) => update(i, { users })}
              fetchOptions={fetchUsers} pasteLookup={pasteUsers} placeholder="Search by name, email, id, or paste emails" renderOption={userOption}
              disabledValues={usedElsewhere(i)} disabledHint="Already in another row for this variable" />
            <RemoteSelect ariaLabel={`Itinerary for row ${i + 1}`} value={r.itinerary} onChange={(itinerary) => update(i, { itinerary })}
              fetchOptions={fetchItineraries} placeholder="Search by headline, location, or id" renderOption={itinOption} />
            <Button aria-label={`Delete row ${i + 1}`} icon={<DeleteOutlined />} disabled={rows.length === 1} onClick={() => onChange(rows.filter((_, idx) => idx !== i))} />
            {problems.length
              ? <div className="rerr">{problems.map((p) => `⚠ ${p}`).join(" · ")}</div>
              : <div className="rok">✓ {r.users.length} user{r.users.length === 1 ? "" : "s"} → {r.itinerary.label}</div>}
            {notRecip.length > 0 && <div className="rerr">⚠ Not in Recipients, won't be emailed: {notRecip.map((u) => u.name || u.label).join(", ")}</div>}
          </div>
        );
      })}
      <div className="note" style={{ marginTop: 4 }}>Note: you can paste a list of email addresses into the users box.</div>
    </div>
  );
};

export default ItineraryMapping;
